import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  ContentTemplate,
  HeaderTemplate,
  KeyValue,
  SingleSelectionRadio,
  SingleSelectionRadioProps,
  Stepper,
  Typography,
  useDataTable,
} from 'web-library';
import { TFunction, useTranslation } from 'react-i18next';
import { Divider } from '@mui/material';


import { makeStyles } from 'tss-react/mui';
import { useGetGroupRegistrationSummary } from '../../../api-refactored/group-exam-registration/useGetRegistrationSummary';
import {
  ExamPaymentMode,
  ExamPaymentModeKey,
  RegistrationRequestTab,
  usePostReviewGroupExamRegistration,
} from '../../../api-refactored/group-exam-registration/usePostReviewGroupExamRegistration';
import ReviewBaseInfo from './ReviewBaseInfo';
import CandidateSubjectListTabs from '../../../components/tabs/CandidateSubjectListTabs';
import ExamFeeDetails from '../../../components/ExamFeeDetails';
import DeclarationAndPaymentButton from './DeclarationAndPaymentButton';
import { candidateOrSubjectListType } from '../../../utils/ComponentUtils';
import { ComponentUsage } from '../../../components/tabs/CandidateSubjectListTabs/model';
import GroupExamRegistrationTitleHeader from '../../../components/GroupExamRegistrationTitleHeader';
import DraftTimeStamp from '../../../components/DraftTimeStamp';
import { useGetPaymentAccess } from '../../../api-refactored/financial/useGetPaymentAccess';

interface CoordinatorReviewRegistrationPageProps {
  routeSteps: { label: string }[];
}

const useStyles = makeStyles()((theme) =>
({
  root: {
    '& .MuiPaper-root.MuiStepper-root': {
      [theme.breakpoints.up('md')]: {
        marginLeft: '-70px',
      },
    },
  },
  gstRegNoBox: { marginTop: theme.spacing(4) },
  divider: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
  },
  subsection: {
    marginBottom: theme.spacing(3),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(6),
  },
  backButton: {
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2),
    },
    [theme.breakpoints.up('sm')]: {
      marginRight: theme.spacing(2),
    },
  },
}));
const checkAvailablePaymentMode = (paymentDataOption: ExamPaymentMode): [] | ExamPaymentModeKey[] =>
  (Object.keys(paymentDataOption) as ExamPaymentModeKey[]).filter((key) => paymentDataOption[key]);

const transformPayOption = (
  paymentDataOption: ExamPaymentMode,
  t: TFunction<'translation'>
): SingleSelectionRadioProps['data'] =>
  checkAvailablePaymentMode(paymentDataOption).map((key: ExamPaymentModeKey) => {
    if (key === ExamPaymentModeKey.bankTransferOption) {
      return {
        label: t('exam->>ReviewExam.BankTransfer'),
        value: key,
      };
    }
    if (key === ExamPaymentModeKey.coordinatorPayOption) {
      return {
        label: t('exam->>ReviewExam.PayForCandidates'),
        value: key,
      };
    }
    if (key === ExamPaymentModeKey.candidatePayOption) {
      return {
        label: t('exam->>ReviewExam.CandidatesMakePayment'),
        additionalLabel: t('exam->>ReviewExam.CandidatesMakePaymentDescription'),
        value: key,
      };
    }
    return {
      label: '',
      additionalLabel: '',
      value: ExamPaymentModeKey.noPaymentModeSelected,
    };
  });

const CoordinatorReviewRegistration = ({ routeSteps }: CoordinatorReviewRegistrationPageProps) => {
  const { uuid: applicationUuid } = useParams<any>();
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [selectedTab, setSelectedTab] = useState(0);
  const { data: summaryData } = useGetGroupRegistrationSummary({
    uuid: applicationUuid,
    refetchInterval: undefined,
  });
  const getCandidateOrSubjectType = candidateOrSubjectListType(selectedTab);
  const { state: candidateTabTableState, dispatch: candidateTabTableDispatch } = useDataTable();
  const [selectedPaymentMode, setSelectedPaymentMode] = useState<ExamPaymentModeKey>(
    ExamPaymentModeKey.noPaymentModeSelected
  );

  const postReviewMutation = usePostReviewGroupExamRegistration({
    defaultOnSuccessInput: {
      getCandidateOrSubjectType,
      tableDispatch: candidateTabTableDispatch,
    },
  });
  const {
    data: reviewBaseInfo,
    mutate: postReviewGroupExamRegistrationMutate,
  } = postReviewMutation;

  const retrieveCandidateTabs = useCallback(() => {
    postReviewGroupExamRegistrationMutate({
      applicationUuid,
      apiPathParam: {
        order: candidateTabTableState.order,
        page: candidateTabTableState.page,
        pageSize: candidateTabTableState.size,
      },
      tab: RegistrationRequestTab.CANDIDATE_LIST,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    retrieveCandidateTabs();
  }, [retrieveCandidateTabs]);

  const { data: paymentAccessDetails } = useGetPaymentAccess();

  const paymentDataOptions = {
    /** Order is important here to reflect in the UI, DO NOT change the order * */
    bankTransferOption: paymentAccessDetails?.bankTransferMethod,
    coordinatorPayOption:
      paymentAccessDetails?.creditCardMethod || paymentAccessDetails?.payNowMethod,
    candidatePayOption: paymentAccessDetails?.candidatePayMethod,
  } as ExamPaymentMode;

  const hasNoPaymentOptions =
    checkAvailablePaymentMode(paymentDataOptions).length === 0 ||
    reviewBaseInfo?.payment?.totalFee === '0.00';

  const isIPsle =
    reviewBaseInfo?.groupedLevelFees?.length &&
    reviewBaseInfo?.groupedLevelFees.filter((item) => item.examLevel === 'iPSLE').length;

  return (
    <Box className={classes.root}>
      <GroupExamRegistrationTitleHeader
        examRegistrationType={summaryData?.examRegistrationType}
        t={t}
      />
      <HeaderTemplate>
        <KeyValue
          keyText={t('exam->>RegisterExamInstructions.Exam Level')}
          valueText={summaryData?.examType.description || ''}
        />
        <Stepper activeStep={2} steps={routeSteps} />
      </HeaderTemplate>
      {reviewBaseInfo && (
        <>
          <ContentTemplate type="full">
            <ReviewBaseInfo reviewBaseInfo={reviewBaseInfo} />
            <CandidateSubjectListTabs
              tabStateHandler={{ selectedTab, setSelectedTab }}
              applicationUuid={applicationUuid}
              getCandidateOrSubjectType={getCandidateOrSubjectType}
              candidateDataTableHandler={{ candidateTabTableState, candidateTabTableDispatch }}
              useMutationHook={postReviewMutation}
              componentUsageFor={ComponentUsage.REVIEW}
            />
            {(!isIPsle || reviewBaseInfo.payment.gstIndicator) && reviewBaseInfo.gstRegNo && (
              <Box className={classes.gstRegNoBox}>
                <KeyValue
                  id="gstRegistrationNumber"
                  keyText={t('components->>SelectedSubjectReview.GST Reg No.')}
                  valueText={reviewBaseInfo.gstRegNo}
                />
              </Box>
            )}
            <Divider className={classes.divider} />
            <Box id="grouped-exam-fees">
              <Typography variant="h3" className={classes.subsection}>
                {t('exam->>ReviewExam.ExamFees')}
              </Typography>
              <ExamFeeDetails
                groupedLevelFees={reviewBaseInfo.groupedLevelFees}
                payment={reviewBaseInfo.payment}
              />
            </Box>
            {checkAvailablePaymentMode(paymentDataOptions).length > 0 &&
              reviewBaseInfo.payment.totalFee !== '0.00' && (
                <Box id="payment-mode">
                  <Divider className={classes.divider} />
                  <Typography variant="h3" className={classes.subsection}>
                    {t('exam->>ReviewExam.Payment Mode')}
                  </Typography>
                  <SingleSelectionRadio
                    id="single-selection-radio-payment-mode"
                    data={transformPayOption(paymentDataOptions, t)}
                    onChange={(event) =>
                      setSelectedPaymentMode(event.target.value as ExamPaymentModeKey)
                    }
                    value={selectedPaymentMode}
                  />
                </Box>
              )}

            <Divider className={classes.divider} />
            <DeclarationAndPaymentButton
              styling={{ classes, t }}
              selectedPaymentMode={selectedPaymentMode}
              hasNoPaymentOptions={hasNoPaymentOptions}
              totalFee={reviewBaseInfo.payment.totalFee}
              examType={summaryData?.examType.code}
              examRegistrationType={summaryData?.examRegistrationType}
            />
            {summaryData && <DraftTimeStamp savedAt={summaryData.lastUpdatedTime} />}
          </ContentTemplate>
        </>
      )}
    </Box>
  );
};

export default CoordinatorReviewRegistration;
